import { useCallback, useEffect, useRef, useState } from "react";
import { AuthImg } from "@/components/AuthImg";
import { ZoomIn, ZoomOut, RotateCw, Maximize2 } from "lucide-react";

// THE ONE PHOTO SURFACE. EVERY PHOTO IN THE APP OPENS ON THIS.
//
// HIS INSTRUCTION, 2026-10-15, from the truck page: *"Tapping a photo does NOT open the good
// viewer — I can't rotate it, grab and pan it, or zoom properly. Every place a photo can be opened
// should use that SAME viewer — one implementation, not several."*
//
// He was right, and it was worse than he could see from one screen. There were FOUR photo
// implementations: the Identify workspace's viewer (zoom, rotate, drag — the good one), the truck
// page's gallery (a zoom toggle and nothing else), the shared lightbox behind every thumbnail strip
// (paging only — no zoom at all), and the Ask-AI panel's own copy of the good one. Which viewer a
// crew member got depended on which screen they happened to tap a photo from.
//
// Now there is one: this surface does the looking (zoom, rotate, drag to pan, pinch, double-tap),
// `PhotoLightbox` wraps it for full-screen with paging, and every screen opens that. A gate test
// (`test_one_photo_viewer_everywhere.py`) fails the build if a new screen grows its own.
//
// BUILT FOR A GLOVE ON A TABLET, not for a mouse: pointer events (so touch, pen and mouse are the
// same code path), `touch-action: none` so a drag pans the photo instead of scrolling the page, and
// two-finger pinch. The controls stay big and sit out of the way of the photo.

const STEPS = [1, 1.5, 2.2, 3.2, 4.5];
const MAX = STEPS[STEPS.length - 1];

export function usePhotoGestures(resetKey) {
  const [scale, setScale] = useState(1);
  const [rot, setRot] = useState(0);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const pointers = useRef(new Map());
  const drag = useRef(null);
  const pinch = useRef(null);
  const moved = useRef(false);

  // A NEW PHOTO IS A NEW LOOK: zoom, angle and position start fresh, or the next photo arrives
  // already shoved off-screen by the last one.
  useEffect(() => { setScale(1); setRot(0); setPan({ x: 0, y: 0 }); }, [resetKey]);

  const clamp = useCallback((s) => Math.min(MAX, Math.max(1, s)), []);
  const setZoom = useCallback((s) => {
    const next = clamp(s);
    setScale(next);
    if (next === 1) setPan({ x: 0, y: 0 });     // fit again = centred again
  }, [clamp]);

  const zoomIn = useCallback(() => setZoom(STEPS.find((s) => s > scale + 0.01) || MAX), [scale, setZoom]);
  const zoomOut = useCallback(() => setZoom([...STEPS].reverse().find((s) => s < scale - 0.01) || 1), [scale, setZoom]);
  const rotate = useCallback(() => setRot((r) => (r + 90) % 360), []);
  const reset = useCallback(() => { setScale(1); setRot(0); setPan({ x: 0, y: 0 }); }, []);
  const toggleZoom = useCallback(() => setZoom(scale > 1 ? 1 : 2.2), [scale, setZoom]);

  const onPointerDown = (e) => {
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    moved.current = false;
    if (pointers.current.size === 2) {
      const [a, b] = [...pointers.current.values()];
      pinch.current = { dist: Math.hypot(a.x - b.x, a.y - b.y) || 1, base: scale };
      drag.current = null;
      return;
    }
    if (scale > 1) {
      drag.current = { sx: e.clientX - pan.x, sy: e.clientY - pan.y };
      // Keeping the drag alive when the finger slides off the photo is a nicety, not the feature:
      // some pointers cannot be captured, and that must never throw into the page.
      try { e.currentTarget.setPointerCapture?.(e.pointerId); } catch { /* not capturable */ }
    }
  };

  const onPointerMove = (e) => {
    if (!pointers.current.has(e.pointerId)) return;
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (pinch.current && pointers.current.size === 2) {
      const [a, b] = [...pointers.current.values()];
      const dist = Math.hypot(a.x - b.x, a.y - b.y) || 1;
      moved.current = true;
      setZoom(pinch.current.base * (dist / pinch.current.dist));
      return;
    }
    if (!drag.current) return;
    moved.current = true;
    setPan({ x: e.clientX - drag.current.sx, y: e.clientY - drag.current.sy });
  };

  const endPointer = (e) => {
    pointers.current.delete(e.pointerId);
    if (pointers.current.size < 2) pinch.current = null;
    if (!pointers.current.size) drag.current = null;
  };

  // A DRAG IS NOT A TAP. Letting go after panning must not also toggle the zoom, or the photo
  // jumps the moment you stop moving it.
  const onClick = () => { if (moved.current) { moved.current = false; return; } toggleZoom(); };
  const onWheel = (e) => { setZoom(scale * (e.deltaY < 0 ? 1.15 : 1 / 1.15)); };

  return {
    scale, rot, pan, zoomed: scale > 1,
    zoomIn, zoomOut, rotate, reset, toggleZoom, setZoom,
    style: { transform: `translate(${pan.x}px, ${pan.y}px) scale(${scale}) rotate(${rot}deg)` },
    surface: { onPointerDown, onPointerMove, onPointerUp: endPointer, onPointerCancel: endPointer,
               onClick, onWheel, style: { touchAction: "none" } },
  };
}

// The controls, worded for the floor and sized for a glove. Shown on every photo surface.
export function PhotoControls({ g, testidPrefix, className = "" }) {
  const btn = "inline-flex items-center justify-center w-11 h-11 rounded-full bg-white/90 text-slate-700 hover:bg-white shadow border border-slate-200/60 disabled:opacity-40";
  return (
    <div className={`flex gap-2 ${className}`} onClick={(e) => e.stopPropagation()}
      onPointerDown={(e) => e.stopPropagation()} data-testid={`${testidPrefix}-controls`}>
      <button type="button" data-testid={`${testidPrefix}-zoom-out`} className={btn}
        disabled={g.scale <= 1} onClick={g.zoomOut} title="Zoom out (−)">
        <ZoomOut className="w-5 h-5" />
      </button>
      <button type="button" data-testid={`${testidPrefix}-zoom-in`} className={btn}
        disabled={g.scale >= MAX} onClick={g.zoomIn} title="Zoom in (+)">
        <ZoomIn className="w-5 h-5" />
      </button>
      <button type="button" data-testid={`${testidPrefix}-rotate`} className={btn}
        onClick={g.rotate} title="Rotate 90° (R)">
        <RotateCw className="w-5 h-5" />
      </button>
      <button type="button" data-testid={`${testidPrefix}-reset`} className={btn}
        disabled={g.scale === 1 && g.rot === 0} onClick={g.reset} title="Fit to screen (0)">
        <Maximize2 className="w-5 h-5" />
      </button>
    </div>
  );
}

// The photo itself on its gesture surface. `controls` can be placed by the caller instead when the
// layout needs them somewhere specific (the lightbox puts them on the bottom bar).
export function PhotoStage({ fileId, alt = "photo", testidPrefix = "photo-stage", gestures,
                            className = "", imgClassName = "", onState, reserve, controls = true,
                            missing = false, children }) {
  const own = usePhotoGestures(fileId);
  const g = gestures || own;
  return (
    <div data-testid={`${testidPrefix}-stage`}
      className={`relative w-full h-full flex items-center justify-center overflow-hidden select-none
        ${g.zoomed ? "cursor-grab active:cursor-grabbing" : "cursor-zoom-in"} ${className}`}
      {...g.surface}>
      <AuthImg key={fileId} fileId={fileId} thumb={false} draggable={false} alt={alt}
        reserve={reserve} onState={onState} style={g.style} missing={missing} big
        className={`max-w-full max-h-full object-contain pointer-events-none transition-transform duration-100 ${imgClassName}`} />
      {g.zoomed && (
        <span data-testid={`${testidPrefix}-pan-hint`}
          className="absolute bottom-3 left-3 text-[11px] font-mono px-2 py-1 rounded-full bg-slate-900/70 text-white pointer-events-none">
          Drag to move · pinch or scroll to zoom
        </span>
      )}
      {controls && <PhotoControls g={g} testidPrefix={testidPrefix} className="absolute bottom-3 right-3" />}
      {children}
    </div>
  );
}

export const PHOTO_ZOOM_MAX = MAX;
